import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import NotificationsIcon from "@mui/icons-material/Notifications";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { countries as countriesList } from "countries-list";
import BottomNav from "./BottomNav";
import axiosInstance from "../api/axiosInstance";
import { requestNotificationPermission, initializePushNotifications } from "../firebase";
import { CircularProgress } from '@mui/material';
import "../styles/styles.css";
import { useAuth } from '../context/AuthContext'; // adjust path if needed


export default function Home() {
  const [feed, setFeed] = useState([]);
  const [mixedFeed, setMixedFeed] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const limit = 10;
  const { userId, accessToken, initialized  } = useAuth();
  const [selectedView, setSelectedView] = useState("For You");

  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
  const [availableCountries, setAvailableCountries] = useState([]);
  const [availableCities, setAvailableCities] = useState([]);

  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedCity, setSelectedCity] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const [viewerDismissedKey, setViewerDismissedKey] = useState(null);
  const viewerMountedRef = useRef(false);

  const getPostId = (post) => post?.trippin_post_id || post?.id;


  const getCountryName = (isoCode) => {
    return countriesList[isoCode]?.name || isoCode || "Unknown Country";
  };

  const handleViewChange = async (event) => {
    const value = event.target.value;
    setSelectedView(value);

    setPage(1);
    setFeed([]);
    setHasMore(true);

    if (value === "Country") {
      await fetchCountries();
      setSelectedCountry("");
      setSelectedCity("");
      setAvailableCities([]);
      setIsFilterDialogOpen(true);
    }
    else {
      fetchFeed(value, 1, null, null);
    }
  };

  const fetchFeed = async (
    type = "For You",
    pageNum = 1,
    country = null,
    city = null
  ) => {
    try {
      setLoading(true);
      setError(null);

      let feedTypeParam = "";
      if (type === "For You") feedTypeParam = "forYou";
      else if (type === "Following") feedTypeParam = "following";
      else if (type === "Country") feedTypeParam = "country";

      let url = `/feed?type=${feedTypeParam}&page=${pageNum}&limit=${limit}`;

      if (country) {
        url += `&country=${country}`;
      }
      if (city) {
        url += `&city=${encodeURIComponent(city)}`;
      }

      const resp = await axiosInstance.get(url);
      const data = resp.data;
      // console.log("Fetched feed data:", data);
      const newFeed = pageNum === 1 ? data.feed : [...feed, ...data.feed];
      setFeed(newFeed);
      // Record organic consumption delta
      try {
        const delta = (data.feed || []).length;
        if (delta > 0) {
          // await recordConsumption({ userId, placement: 'home', organic_delta: delta, countryCode: selectedCountry || undefined });
        }
      } catch (_) {}

      // Client-side ad mixing: get injection positions and ads
      // Ad mixing temporarily disabled to avoid undefined references
      setMixedFeed(newFeed);

      setHasMore(data.hasMore);
    } catch (err) {
      console.error("Error fetching feed:", err);
      setError("Failed to fetch feed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleLoadMore = () => {
    if (!hasMore) return;
    setPage((prev) => prev + 1);
  };

  const fetchCountries = async () => {
    try {
      const response = await axiosInstance.get("/feed/countries");
      setAvailableCountries(response.data.countries);
    } catch (err) {
      console.error("Error fetching countries:", err);
    }
  };

  const fetchCities = async (countryISO) => {
    if (!countryISO) return;
    try {
      const response = await axiosInstance.get(`/feed/cities?country=${countryISO}`);
      setAvailableCities(response.data.cities);
    } catch (err) {
      console.error("Error fetching cities:", err);
    }
  };

  const handleCountryChange = async (e) => {
    const chosenCountry = e.target.value;
    setSelectedCountry(chosenCountry);
    setSelectedCity("");
    setAvailableCities([]);

    if (chosenCountry) {
      await fetchCities(chosenCountry);
    }
  };

  const handleCityChange = (e) => {
    setSelectedCity(e.target.value);
  };

  const handleApplyCountryFilter = () => {
    setIsFilterDialogOpen(false);
    setPage(1);
    setFeed([]);
    setHasMore(true);

    fetchFeed("Country", 1, selectedCountry, selectedCity);
  };

  const handleRetry = async () => {
    setError(null);
    setPage(1);
    setFeed([]);
    setHasMore(true);

    if (selectedView === "Country") {
      if (!selectedCountry) {
        await fetchCountries();
        setIsFilterDialogOpen(true);
        return;
      }
      fetchFeed("Country", 1, selectedCountry, selectedCity);
      return;
    }

    fetchFeed(selectedView, 1, null, null);
  };


useEffect(() => {
  if (!initialized) return; // ✅ wait for auth layer
  if (!userId) navigate('/login', { replace: true });
}, [userId, initialized, navigate]);


  useEffect(() => {
    const setupNotifications = async () => {
      if (!userId) return;

      try {
        const permission = await Notification.requestPermission();
        if (permission === "granted") {
          console.log("Web notifications permission granted.");
          await requestNotificationPermission();
        } else {
          console.log("Web notifications permission denied.");
        }
        await initializePushNotifications(accessToken);
      } catch (error) {
        console.error("Error setting up notifications:", error);
      }
    };

    setupNotifications();
  }, [userId, accessToken]);


  useEffect(() => {
    if (userId) {
      fetchFeed("For You", 1, null, null);
    }
  }, [userId]);

  useEffect(() => {
    if (page > 1) {
      if (selectedView === "For You" || selectedView === "Following") {
        fetchFeed(selectedView, page, null, null);
      }
      else if (selectedView === "Country" && selectedCountry) {
        fetchFeed("Country", page, selectedCountry, selectedCity);
      }
    }
  }, [page]);

  const listToRender = mixedFeed.length ? mixedFeed : feed;
  const contentPosts = listToRender
    .filter((item) => !(item && (item.__kind === "ad" || item.__kind === "ext_ad")))
    .filter(Boolean);
  const trippinPostIds = contentPosts.map(getPostId).filter(Boolean);
  const isInitialLoading = loading && feed.length === 0;
  const isEmpty = !loading && !error && trippinPostIds.length === 0;

  const feedKey = `${selectedView}|${selectedCountry || ""}|${selectedCity || ""}`;

  const openTrippinViewer = async (selectedId) => {
    const domNode = document.getElementById('trippin-root');
    if (!domNode) return;
    if (!selectedId) return;

    const { mount, unmount } = await import('./TrippinPost/index.js');

    viewerMountedRef.current = true;

    mount(domNode, {
      trippin_ids: trippinPostIds,
      selectedTrippinId: selectedId,
      source: "home",
      offset: Math.max(0, trippinPostIds.indexOf(selectedId)),
      onExit: () => {
        try { unmount(); } catch (_) {}
        viewerMountedRef.current = false;
        setViewerDismissedKey(feedKey);
      },
    });
  };

  // Auto-open the viewer when we have content for the current feed context.
  useEffect(() => {
    if (isInitialLoading || error) return;
    if (!trippinPostIds.length) return;
    if (viewerMountedRef.current) return;
    if (viewerDismissedKey === feedKey) return; // user closed it for this view

    openTrippinViewer(trippinPostIds[0]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialLoading, error, feedKey, trippinPostIds.length]);

  const getEmptyMessage = () => {
    if (selectedView === "Following") return "No trippins from people you follow yet";
    if (selectedView === "Country") return "No trippins found for this location";
    return "No trippins to show yet";
  };

  return (
    <div className="home-container">
      <div className="top-nav">
        <div className="dropdown-container">
          <Select
            value={selectedView}
            onChange={handleViewChange}
            variant="standard"
            disableUnderline
            displayEmpty
            sx={{
              fontSize: "16px",
              fontWeight: "bold",
              color: "inherit",
              width: "100px",
              "& .MuiSelect-select": {
                display: "flex",
                alignItems: "center",
                padding: "0 4px",
              },
              "& .MuiSelect-icon": {
                marginLeft: "4px",
                color: "inherit",
              },
            }}
            IconComponent={KeyboardArrowDownIcon}
          >
            <MenuItem value="For You">For You</MenuItem>
            <MenuItem value="Following">Following</MenuItem>
            <MenuItem value="Country">Country</MenuItem>
          </Select>
        </div>

        <div className="icons-container">
          <SearchIcon
            className="icon"
            onClick={() => navigate("/search")}
          />
          <NotificationsIcon
            className="icon"
            onClick={() => navigate("/notifications")}
          />
        </div>
      </div>

      <div className="feed-container">
        {isInitialLoading && (
          <div
            style={{
              minHeight: "50vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress size={40} sx={{ color: "#d1d5db" }} />
          </div>
        )}

        {!isInitialLoading && error && (
          <div
            style={{
              minHeight: "50vh",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              padding: "24px",
              gap: "12px",
            }}
          >
            <div style={{ color: "#d1d5db" }}>{error}</div>
          </div>
        )}

        {!isInitialLoading && !error && isEmpty && (
          <div
            style={{
              minHeight: "50vh",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              padding: "24px",
              gap: "12px",
            }}
          >
            <div style={{ color: "#d1d5db" }}>{getEmptyMessage()}</div>
          </div>
        )}

        {!isInitialLoading && !error && !isEmpty && (
          <>
            {viewerDismissedKey === feedKey && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  padding: "16px 0",
                }}
              >
                <Button variant="contained" onClick={() => openTrippinViewer(trippinPostIds[0])}>
                  Open Feed
                </Button>
              </div>
            )}

            {/* Example load-more button, or you could do infinite scroll */}
            {hasMore && !loading && (
              <Button onClick={handleLoadMore} variant="contained" sx={{ mt: 2 }}>
                Load More
              </Button>
            )}
            {loading && feed.length > 0 && <p>Loading more...</p>}
          </>
        )}
      </div>

      <Dialog
        open={isFilterDialogOpen}
        onClose={() => setIsFilterDialogOpen(false)}
        slotProps={{
          paper: {
            sx: {
              borderRadius: "12px",
              padding: "14px",
              maxWidth: "400px",
              width: "100%",
            },
          },
        }}
      >
        <DialogTitle>Filter by Country/City</DialogTitle>
        <DialogContent>
          {/* COUNTRY SELECT */}
          <Select
            fullWidth
            value={selectedCountry}
            onChange={handleCountryChange}
            displayEmpty
            variant="outlined"
            sx={{
              marginBottom: "1rem",
              backgroundColor: "#f9f9f9",
              border: "none",
              padding: "4px 0",
              "& .MuiSelect-select": {
                padding: "4px 8px",
              },
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
            }}
          >
            <MenuItem value="">Select Country</MenuItem>
            {availableCountries.map((countryIso) => (
              <MenuItem key={countryIso} value={countryIso}>
                {getCountryName(countryIso.toUpperCase())}
              </MenuItem>
            ))}
          </Select>

          {/* CITY SELECT (optional) */}
          {availableCities.length > 0 && (
            <Select
              fullWidth
              value={selectedCity}
              onChange={handleCityChange}
              displayEmpty
              variant="outlined"
              sx={{
                backgroundColor: "#f9f9f9",
                border: "none",
                padding: "4px 0",
                "& .MuiSelect-select": {
                  padding: "4px 8px",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
              }}
            >
              <MenuItem value="">All Cities</MenuItem>
              {availableCities.map((city) => (
                <MenuItem key={city} value={city}>
                  {city}
                </MenuItem>
              ))}
            </Select>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsFilterDialogOpen(false)}
            sx={{ textTransform: "none", color: "#2c3e50" }}
          >
            Close
          </Button>
          <Button
            onClick={handleApplyCountryFilter}
            variant="contained"
            sx={{ textTransform: "none" }}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      <BottomNav />
    </div>
  );
}
